import { formatNumbers, LuneThemeNew, Text } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import { FC, useMemo } from 'react'

const { palette } = LuneThemeNew

type WtwBreakdownData = {
    wtt: string
    ttw: string
    unknown: string
}

const Legend: FC<{
    data: {
        label: string
        value: number | undefined
        color: string
    }[]
}> = ({ data }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {data.map((d) => (
                <Box
                    key={d.label}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: d.color,
                            flexShrink: 0,
                        }}
                    />
                    <Text variant={'body3'}>
                        {d.label}: {d.value && `${formatNumbers(d.value, 2)} tCO₂e`}
                    </Text>
                </Box>
            ))}
        </Box>
    )
}

const Charts: FC<{
    data: {
        label: string
        value: number | undefined
        color: string
    }[]
}> = ({ data }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 3,
                flexDirection: 'column',
                position: 'relative',
                '&:after': {
                    content: '""',
                    height: '100%',
                    width: '1px',
                    backgroundColor: palette.Surface600,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                },
            }}
        >
            {data.map((d) => (
                <Box
                    key={d.label}
                    sx={{
                        width: '100%',
                        height: '12px',
                        backgroundColor: palette.Surface300,
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            width: d.value ? `${d.value}%` : 0,
                            height: '100%',
                            backgroundColor: d.color,
                            transition: 'width 0.3s ease',
                        }}
                    />
                </Box>
            ))}
        </Box>
    )
}

const WtwBreakdown: FC<{
    breakdown: WtwBreakdownData | undefined
}> = ({ breakdown }) => {
    const { wttValue, ttwValue, wttPercentage, ttwPercentage, hasData } = useMemo(() => {
        if (!breakdown) {
            return {
                wttValue: 0,
                ttwValue: 0,
                wttPercentage: 0,
                ttwPercentage: 0,
                hasData: false,
            }
        }

        const wtt = parseFloat(breakdown.wtt)
        const ttw = parseFloat(breakdown.ttw)
        const total = wtt + ttw

        if (total === 0) {
            return {
                wttValue: 0,
                ttwValue: 0,
                wttPercentage: 0,
                ttwPercentage: 0,
                hasData: false,
            }
        }

        return {
            wttValue: wtt,
            ttwValue: ttw,
            wttPercentage: (wtt / total) * 100,
            ttwPercentage: (ttw / total) * 100,
            hasData: true,
        }
    }, [breakdown])

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                height: '100%',
                justifyContent: 'center',
            }}
        >
            <Legend
                data={[
                    {
                        label: 'Well-To-Tank',
                        value: hasData ? wttValue : undefined,
                        color: palette.Data100,
                    },
                    {
                        label: 'Tank-To-Wheel',
                        value: hasData ? ttwValue : undefined,
                        color: palette.Data300,
                    },
                ]}
            />
            <Charts
                data={[
                    {
                        label: 'Well-To-Tank',
                        value: hasData ? wttPercentage : undefined,
                        color: palette.Data100,
                    },
                    {
                        label: 'Tank-To-Wheel',
                        value: hasData ? ttwPercentage : undefined,
                        color: palette.Data300,
                    },
                ]}
            />
        </Box>
    )
}

export default WtwBreakdown
